/**
 * Helpers for keeping workflow-input references canonical in emitted specs.
 *
 * The runtime resolves workflow inputs through the same variable registry as
 * captures and field values, so stored config should use `$var.<inputName>`.
 * Older/planner-authored specs may contain bare `$inputName`; normalize those
 * at spec boundaries instead of teaching the runner another reference syntax.
 */

/** Normalize bare declared workflow-input references to `$var.<inputName>`. */
export function normalizeDeclaredInputReferences(
  value: string,
  inputNames: ReadonlySet<string>
): string {
  if (inputNames.size === 0 || !value.includes('$')) return value;

  return value.replace(
    /(^|[^\\])\$([A-Za-z_][A-Za-z0-9_-]*)/g,
    (match: string, prefix: string, name: string, offset: number, full: string) => {
      if (!inputNames.has(name)) return match;

      const next = full[offset + match.length];
      const afterDot = full[offset + match.length + 1];
      if (next === '.' && afterDot !== undefined && /[A-Za-z0-9_-]/.test(afterDot)) {
        return match;
      }

      return `${prefix}$var.${name}`;
    }
  );
}
